import React, { useContext, useState } from 'react';
import { useUpdateQuizMutation } from '../../services/SkillsManagement';
import { useDispatch } from 'react-redux';
import { UPDATE_QUIZ } from '../../Reducers/Actions';
import EditIcon from '@mui/icons-material/Edit';
import { MenuItem, ListItemIcon, ListItemText, Grid } from '@mui/material';
import MuiDialog from '../../SystemDesign/MuiDialog';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import FormInput from '../../SystemDesign/FormInput';
import Switch from '../../SystemDesign/Switch';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';

const UpdateQuiz = ({ row }) => {
  const [updateQuiz, { isLoading }] = useUpdateQuizMutation();
  const dispatch = useDispatch();
  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Safely access row data
  const quiz = row?.original || {};

  // Form state
  const initialValues = {
    title: quiz.title || '',
    description: quiz.description || '',
    timeLimit: typeof quiz.timeLimit === 'number' ? quiz.timeLimit : 0,
    maxAttempts: typeof quiz.maxAttempts === 'number' ? quiz.maxAttempts : 1,
    shuffleQuestions: Boolean(quiz.shuffleQuestions),
    shuffleOptions: Boolean(quiz.shuffleOptions),
    isActive: quiz.isActive !== undefined ? Boolean(quiz.isActive) : true,
  };
  const [formValues, setFormValues] = useState(initialValues);

  const formValidationSchema = yup.object().shape({
    title: yup.string().required('Title is required'),
    description: yup.string().nullable(),
    timeLimit: yup
      .number()
      .typeError('Time limit must be a number')
      .min(0, 'Must be >= 0')
      .required('Time limit is required'),
    maxAttempts: yup
      .number()
      .typeError('Max attempts must be a number')
      .min(1, 'Must be >= 1')
      .required('Max attempts is required'),
    shuffleQuestions: yup.boolean().required(),
    shuffleOptions: yup.boolean().required(),
    isActive: yup.boolean().required(),
  });

  const getFieldGridProps = (name) => {
    switch (name) {
      case 'title':
      case 'timeLimit':
      case 'maxAttempts':
        return { xs: 12, md: 4, lg: 4 };
      case 'description':
        return { xs: 12, md: 12, lg: 12 };
      case 'isActive':
      case 'shuffleQuestions':
      case 'shuffleOptions':
        return { xs: 12, md: 4, lg: 4 };
      default:
        return { xs: 12 };
    }
  };

  const handleUpdate = async (values) => {
    try {
      const payload = {
        title: values.title.trim(),
        description: values.description?.trim() || '',
        timeLimit: Number(values.timeLimit || 0),
        maxAttempts: Number(values.maxAttempts || 1),
        shuffleQuestions: Boolean(values.shuffleQuestions),
        shuffleOptions: Boolean(values.shuffleOptions),
        isActive: Boolean(values.isActive),
      };

      try {
        const response = await updateQuiz({ id: Number(quiz.quizId), body: payload }).unwrap();

        const updatedForStore = {
          ...quiz,
          ...payload,
          updatedAt: new Date().toISOString(),
        };

        dispatch({ type: UPDATE_QUIZ, payload: updatedForStore });

        let successMessage;
        if (typeof response === 'string') successMessage = response;
        else if (response && typeof response === 'object' && response.message) successMessage = response.message;
        else successMessage = `Quiz "${payload.title}" updated successfully`;

        setInfoAlert({ severity: 'success', message: successMessage });
        setOpenSnackBar(true);
        setDialogOpen(false);
        return true;
      } catch (parseError) {
        if (parseError?.originalStatus === 200) {
          const updatedForStore = {
            ...quiz,
            ...payload,
            updatedAt: new Date().toISOString(),
          };
          dispatch({ type: UPDATE_QUIZ, payload: updatedForStore });

          const successMessage = parseError.data || `Quiz "${payload.title}" updated successfully`;
          setInfoAlert({ severity: 'success', message: successMessage });
          setOpenSnackBar(true);
          setDialogOpen(false);
          return true;
        }
        throw parseError;
      }
    } catch (error) {
      console.error('Failed to update quiz:', error);
      setInfoAlert({ severity: 'error', message: error?.data?.message || 'An error occurred' });
      setOpenSnackBar(true);
      return false;
    }
  };

  const triggerButton = (
    <MenuItem onClick={() => setDialogOpen(true)}>
      <ListItemIcon>
        <EditIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>Edit</ListItemText>
    </MenuItem>
  );

  const dialogContent = (
    <Formik
      initialValues={formValues}
      validationSchema={formValidationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      enableReinitialize={true}
      onSubmit={handleUpdate}
    >
      {(formik) => (
        <Form id="update-quiz-form">
          <Grid container spacing={2} marginTop={2}>
            <Grid item size={{ ...getFieldGridProps('title') }}>
              <FormInput
                type="text"
                name="title"
                label="Quiz Title"
                required={true}
                value={formik.values.title}
                onChange={(e) => {
                  formik.handleChange(e);
                  setFormValues({ ...formValues, title: e.target.value });
                }}
              />
            </Grid>

            <Grid item size={{ ...getFieldGridProps('timeLimit') }}>
              <FormInput
                type="number"
                name="timeLimit"
                label="Time Limit (minutes)"
                required={true}
                value={formik.values.timeLimit}
                onChange={(e) => {
                  formik.setFieldValue('timeLimit', e.target.value === '' ? '' : Number(e.target.value));
                  setFormValues({ ...formValues, timeLimit: e.target.value === '' ? '' : Number(e.target.value) });
                }}
              />
            </Grid>

            <Grid item size={{ ...getFieldGridProps('maxAttempts') }}>
              <FormInput
                type="number"
                name="maxAttempts"
                label="Maximum Attempts"
                required={true}
                value={formik.values.maxAttempts}
                onChange={(e) => {
                  formik.setFieldValue('maxAttempts', e.target.value === '' ? '' : Number(e.target.value));
                  setFormValues({ ...formValues, maxAttempts: e.target.value === '' ? '' : Number(e.target.value) });
                }}
              />
            </Grid>

            <Grid item size={{ ...getFieldGridProps('description') }}>
              <FormInput
                name="description"
                label="Description"
                required={false}
                value={formik.values.description}
                onChange={(e) => {
                  formik.handleChange(e);
                  setFormValues({ ...formValues, description: e.target.value });
                }}
                multiline
                minRows={3}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item size={{ ...getFieldGridProps('isActive') }}>
              <Switch name="isActive" label="Quiz Active" />
            </Grid>
            <Grid item size={{ ...getFieldGridProps('shuffleQuestions') }}>
              <Switch name="shuffleQuestions" label="Shuffle Questions" />
            </Grid>
            <Grid item size={{ ...getFieldGridProps('shuffleOptions') }}>
              <Switch name="shuffleOptions" label="Shuffle Options" />
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );

  return (
    <>
      {triggerButton}

      <MuiDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        icon={<EditIcon />}
        title="Update Quiz"
        subtitle={`Update quiz ${quiz.title || ''}.`}
        content={dialogContent}
        existAction={true}
        actionTitle="Update Quiz"
        actionType="submit"
        formId="update-quiz-form"
        isLoading={isLoading}
      />
    </>
  );
};

// Add static properties for menu integration
UpdateQuiz.menuIcon = <EditIcon fontSize="small" />;
UpdateQuiz.menuLabel = 'Edit';

export default UpdateQuiz;
